<template>
  <SubjectLayout
    :subject="subject"
    :validateError="validateError"
    class="collection-select"
    :class="{ 'collection-select--disabled': computedDisabled }"
  >
    <div class="collection-select__loading-wrapper" v-if="isFetchingData">
      <b-spinner style="border-width: 2px" />
    </div>
    <template v-else>
      <SharedSelect
        v-if="presentationType === 'select'"
        v-model="selectedSelectValue"
        :options="options"
        :hasBorder="false"
        :placeholder="placeholder"
        :disabled="subject.disabled"
        @input="handleSelectValueChanged"
      />
      <div v-if="presentationType === 'radio'" class="row" style="margin: 0 -0.5rem">
        <div
          class="col-12 col-sm-6 px-2"
          v-for="(option, optionIndex) in options"
          :key="optionIndex"
        >
          <SharedRadioButton
            :id="subject.id + '-' + optionIndex"
            :value="optionIndex.toString()"
            :name="subject.id"
            :disabled="option.disabled"
            @change="handleRadioValueChanged(option)"
          >
            {{ option.text }}
          </SharedRadioButton>
        </div>
      </div>
      <div v-if="presentationType === 'checkbox'" class="row" style="margin: 0 -0.5rem">
        <div
          class="col-12 col-sm-6 px-2"
          v-for="(option, optionIndex) in options"
          :key="optionIndex"
        >
          <SharedCheckbox
            v-model="selectedCheckboxValue"
            :id="subject.id + '-' + optionIndex"
            :value="optionIndex.toString()"
            :name="subject.id"
            :disabled="option.disabled"
            @input="handleCheckboxValueChanged"
          >
            {{ option.text }}
          </SharedCheckbox>
        </div>
      </div>
    </template>

  </SubjectLayout>
</template>

<script>
import collectionApi from "@/apis/liff/v2/collection";
import SubjectLayout from "@/components/DynamicForm/SubjectLayout";
import SharedSelect from "@/components/DynamicForm/Select";
import SharedRadioButton from "@/components/DynamicForm/Radio";
import SharedCheckbox from "@/components/DynamicForm/MultiCheckbox";
import Collection from "@/models/Collection.model.js";
import DynamicFormValuePresenter from "@/presenters/DynamicFormValue.presenter"
import _ from "lodash";

export default {
  components: {
    SubjectLayout,
    SharedSelect,
    SharedRadioButton,
    SharedCheckbox,
  },
  props: {
    subject: {
      type: Object,
      required: true,
    },
    // value: [String, Number, Array, Object],
    value: {
      type: Array,
      default: () => {[]},
    },
    validateError: {
      type: Boolean,
    },
    placeholder: {
      type: String,
      required: false,
    },
    branchId: {
      type: String,
      required: false,
    },
    isRelatedToBranch: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    optionDisabler: {
      type: Function,
      default: () => {
        return (option) => false // eslint-disable-line
      },
    },
  },
  data() {
    return {
      collection: null,
      isFetchingData: false,
      selectedSelectValue: null,
      selectedCheckboxValue: [],
    }
  },
  computed: {
    selected: {
      get() {
        if (Array.isArray(this.value)) {
          return this.value;
        }
        return this.value !== null ? [this.value] : [];
      },
      set(v) {
        if (v !== null) {
          this.$emit("input", Array.isArray(v) ? v : [v]);
        } else {
          this.$emit("input", null);
        }
      }
    },
    options() {
      if (this.collection === null) {
        return []
      }

      const collectionModel = new Collection(this.collection)

      return _.get(this.collection, 'items', []).sort((a, b) => a.order - b.order).map((item) => {
        const text = collectionModel.textForOption(item.id)
        const value = DynamicFormValuePresenter.collectionItem(this.subject, item, this.collection)
        const isDisabled = this.optionDisabler({ value, text })
        const reason = isDisabled?.reason
        return {
          value,
          text: !!isDisabled && reason ? `${text} (${reason})` : text,
          disabled: !!isDisabled,
        };
      });
    },
    computedDisabled() {
      return this.disabled || (this.isRelatedToBranch && !this.branchId)
    },
    presentationType() {
      return this.subject?.collection_presentation || 'select'
    },
  },
  methods: {
    handleSelectValueChanged(value) {
      if (value === null) {
        this.selected = []
      } else {
        this.selected = [value]
      }
    },
    handleRadioValueChanged(option) {
      if (option.value === null) {
        this.selected = []
      } else {
        this.selected = [option.value]
      }
    },
    handleCheckboxValueChanged() {
      this.selected = this.options
        .filter((option, index) => this.selectedCheckboxValue.includes(index.toString()))
        .map(option => option.value)
    },
    async fetchCollectionItems() {
      this.isFetchingData = true
      try {
        if (this.isRelatedToBranch) {
          if (this.branchId) {
            const response = await collectionApi.getOptionsForBranch(this.subject.collection_id, this.branchId);
            this.collection = response.data.data;
          } else {
            this.collection = null
          }
        } else {
          const response = await collectionApi.getCollection(this.subject.collection_id);
          this.collection = response.data.data;
        }
      } catch (error) {
        console.error(error);
        this.$swal("錯誤", `讀取 ${this.subject.title} 選項時發生錯誤，請重新整理後再試一次`, "error");
      } finally {
        this.isFetchingData = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.collection-select {
  &__loading-wrapper {
    height: calc(2.6rem + 2px);
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px solid #e5e5ea;
    border-radius: 5px;
    background-color: white;
  }

  &--disabled {
    cursor: not-allowed;
  }
}

::v-deep.collection-select select.s-form-control {
  border: 1px solid #e5e5ea !important;
  border-radius: 5px;
  background-color: white;
}

::v-deep.collection-select--disabled select.s-form-control {
  border: 1px solid #e5e5ea !important;
  background-color: #eee;
  color: #888;
  pointer-events: none;
}
</style>
